// Units per measure (spec 010 FR-034). The server keeps SI units; these
// only change display and entry. Their words come from i18n/common, which
// the apps share (spec 013 FR-010).
import { decimal, integer, t } from '../i18n/index.ts';

export type DistanceUnit = 'km' | 'mi';
export type ElevationUnit = 'm' | 'ft';
// WeightUnit 'st' is stones and pounds, "11 st 4 lb".
export type WeightUnit = 'kg' | 'lb' | 'st';
// HeightUnit 'ftin' is feet and inches, "5 ft 10 in".
export type HeightUnit = 'cm' | 'ftin';
export type TemperatureUnit = 'c' | 'f';

// Units are the units each measure shows in. Distance carries pace and
// speed with it.
export interface Units {
  distance: DistanceUnit;
  elevation: ElevationUnit;
  weight: WeightUnit;
  height: HeightUnit;
  temperature: TemperatureUnit;
}

export type Measure = keyof Units;

export const MEASURES: Measure[] = ['distance', 'elevation', 'weight', 'height', 'temperature'];

export const METRIC: Units = { distance: 'km', elevation: 'm', weight: 'kg', height: 'cm', temperature: 'c' };
export const IMPERIAL: Units = { distance: 'mi', elevation: 'ft', weight: 'lb', height: 'ftin', temperature: 'f' };

// REGIONS is 010 FR-034's table, which the apps share. A region not in it uses
// METRIC.
const US: Units = IMPERIAL;
const GB: Units = { distance: 'mi', elevation: 'm', weight: 'st', height: 'ftin', temperature: 'c' };
const CA: Units = { distance: 'km', elevation: 'm', weight: 'lb', height: 'ftin', temperature: 'c' };
const REGIONS: Record<string, Units> = { US, LR: US, MM: US, GB, CA };

// regionUnits is the Automatic units for a language tag such as "en-GB". A
// tag without a region, such as "en", uses METRIC.
export function regionUnits(tag: string): Units {
  let region: string | undefined;
  try {
    region = new Intl.Locale(tag).region;
  } catch {
    region = undefined;
  }
  return REGIONS[region ?? ''] ?? METRIC;
}

// UNIT_NAMES are each unit's short name, as the settings list them, read
// from the catalog each time so they follow the language.
export const UNIT_NAMES: Record<string, string> = Object.defineProperties(
  {} as Record<string, string>,
  Object.fromEntries(['km', 'mi', 'm', 'ft', 'kg', 'lb', 'st', 'cm', 'ftin', 'c', 'f'].map((u) => [u, { enumerable: true, get: () => t('unitsShort', { unit: u }) }])),
);

// UNIT_CHOICES are the units each measure may take.
export const UNIT_CHOICES: { [M in Measure]: Units[M][] } = {
  distance: ['km', 'mi'],
  elevation: ['m', 'ft'],
  weight: ['kg', 'lb', 'st'],
  height: ['cm', 'ftin'],
  temperature: ['c', 'f'],
};

export const METERS_PER_MILE = 1609.344;
export const FEET_PER_METER = 3.28084;
export const KG_PER_POUND = 0.45359237;
export const CM_PER_INCH = 2.54;

// stonesAndPounds splits a weight into whole stones and whole pounds,
// carrying 14 lb into a stone.
export function stonesAndPounds(kg: number): { st: number; lb: number } {
  const pounds = Math.round(kg / KG_PER_POUND);
  return { st: Math.floor(pounds / 14), lb: pounds % 14 };
}

// feetAndInches splits a height into whole feet and whole inches, carrying
// 12 in into a foot.
export function feetAndInches(cm: number): { ft: number; inch: number } {
  const inches = Math.round(cm / CM_PER_INCH);
  return { ft: Math.floor(inches / 12), inch: inches % 12 };
}

// weightText is "72.4 kg", "159.6 lb", or "11 st 4 lb".
export function weightText(kg: number, unit: WeightUnit): string {
  if (unit === 'st') {
    const { st, lb } = stonesAndPounds(kg);
    return t('unitsWeightSt', { stones: integer(st), pounds: integer(lb) });
  }
  return unit === 'lb' ? t('unitsWeightLb', { weight: decimal(kg / KG_PER_POUND, 1) }) : t('unitsWeightKg', { weight: decimal(kg, 1) });
}

// weightFields are the entry fields for a weight: one number for kg or lb,
// stones and pounds for st. An absent weight is empty.
export function weightFields(kg: number | undefined, unit: WeightUnit): [string, string] {
  if (!kg) return ['', ''];
  if (unit === 'st') {
    const { st, lb } = stonesAndPounds(kg);
    return [String(st), String(lb)];
  }
  const v = unit === 'lb' ? kg / KG_PER_POUND : kg;
  return [String(Math.round(v * 10) / 10), ''];
}

// weightFromFields reads the entry fields back as kilograms: 0 when both are
// empty, null when either is not a number.
export function weightFromFields(unit: WeightUnit, a: string, b: string): number | null {
  const x = fieldNumber(a), y = unit === 'st' ? fieldNumber(b) : 0;
  if (x == null || y == null) return null;
  if (unit === 'st') return (x * 14 + y) * KG_PER_POUND;
  return unit === 'lb' ? x * KG_PER_POUND : x;
}

// heightFields are the entry fields for a height: centimeters, or feet and
// inches.
export function heightFields(cm: number | undefined, unit: HeightUnit): [string, string] {
  if (!cm) return ['', ''];
  if (unit === 'ftin') {
    const { ft, inch } = feetAndInches(cm);
    return [String(ft), String(inch)];
  }
  return [String(Math.round(cm)), ''];
}

// heightFromFields reads the entry fields back as centimeters, as
// weightFromFields does.
export function heightFromFields(unit: HeightUnit, a: string, b: string): number | null {
  const x = fieldNumber(a), y = unit === 'ftin' ? fieldNumber(b) : 0;
  if (x == null || y == null) return null;
  return unit === 'ftin' ? (x * 12 + y) * CM_PER_INCH : x;
}

// fieldNumber is an entry field's number, 0 when empty, null when not a number.
function fieldNumber(s: string): number | null {
  const t = s.trim();
  if (!t) return 0;
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
}
